import { useCallback } from "react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

// useState that optionally mirrors into a URL search param. When the
// value matches the default, the key is omitted entirely.

type Updater<T> = T | ((prev: T) => T);

function isFn<T>(x: Updater<T>): x is (prev: T) => T {
  return typeof x === "function";
}

export interface Codec<T> {
  /** Return null to omit the key from the URL. */
  serialize: (v: T) => string | null;
  parse: (s: string | null) => T;
}

export function useUrlSyncedState<T>(
  key: string,
  defaultValue: T,
  enabled: boolean,
  codec: Codec<T>,
): [T, (updater: Updater<T>) => void] {
  const [params, setParams] = useSearchParams();
  const [local, setLocal] = useState<T>(defaultValue);

  const setUrl = useCallback(
    (updater: Updater<T>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const prevVal = codec.parse(prev.get(key));
          const newVal = isFn(updater) ? updater(prevVal) : updater;
          const serialized = codec.serialize(newVal);
          if (serialized === null) next.delete(key);
          else next.set(key, serialized);
          return next;
        },
        { replace: true },
      );
    },
    [key, codec, setParams],
  );

  if (!enabled) return [local, setLocal];
  return [codec.parse(params.get(key)), setUrl];
}

export function stringCodec<T extends string>(defaultValue: T): Codec<T> {
  return {
    serialize: (v) => (v === defaultValue ? null : v),
    parse: (s) => (s === null ? defaultValue : (s as T)),
  };
}

// Sorted comma-separated list. Empty selection serializes as "" so
// it stays distinct from "default".
export function stringSetCodec(defaultMembers: ReadonlyArray<string>): Codec<Set<string>> {
  const defaultSorted = [...defaultMembers].sort().join(",");
  return {
    serialize: (v) => {
      const sorted = [...v].sort().join(",");
      if (sorted === defaultSorted) return null;
      return sorted;
    },
    parse: (s) => {
      if (s === null) return new Set(defaultMembers);
      if (s === "") return new Set<string>();
      return new Set(s.split(","));
    },
  };
}
